import {
  ArticleUserReactionService,
  ReactArticleService,
  UserService
} from '@atreus/data';
import {
  CommentariesResponse,
  CommentaryWithUserProcessed
} from './NewsShowPage/news.service';
import { createStore } from '@qcx/store';
import { errorsType } from './NewsShowPage/components/Comments/AlertModal';

export interface NewsStoreState {
  isSidebarOpen: boolean;
  isAuthenticated: boolean;
  isLikeActive: boolean;
  isDislikeActive: boolean;
  likes: number;
  dislikes: number;
  comments: {
    commentaries: CommentaryWithUserProcessed[];
    meta: {
      hasMore: boolean;
    };
  };
  articleId: number | undefined;
  userInfo:
    | {
        id?: number;
        name?: string;
        avatarUrl?: string;
      }
    | undefined;
  alertModal: {
    type: errorsType;
    isOpen: boolean;
  };
}

const initialState: NewsStoreState = {
  isLikeActive: false,
  isDislikeActive: false,
  isSidebarOpen: false,
  isAuthenticated: false,
  likes: 0,
  dislikes: 0,
  comments: {
    commentaries: [],
    meta: {
      hasMore: false
    }
  },
  articleId: undefined,
  userInfo: undefined,
  alertModal: {
    type: 'default',
    isOpen: false
  }
};

// TODO: Refactor this component, starting with the backend. The implementation was rushed due to time constraints and it wasn't a high priority at the moment. A detailed review is necessary to enhance its structure and efficiency, ensuring that the backend logic is optimized before making changes to the frontend.

const { Provider, useStore } = createStore(initialState, ({ set, get }) => ({
  toggleSidebar: (value?: boolean) => {
    set(prev => ({
      isSidebarOpen: value === undefined ? !prev.isSidebarOpen : value
    }));
  },

  authenticateUser: async () => {
    try {
      const userService = new UserService({ credentials: 'include' });
      const user = await userService.getAtreusUser();

      set({
        isAuthenticated: !!user,
        userInfo: user
          ? { id: user.id, name: user.name, avatarUrl: user.avatarUrl }
          : undefined
      });
      return true;
    } catch {
      set({
        isAuthenticated: false,
        userInfo: undefined
      });
      return false;
    }
  },

  checkArticleReaction: async (slug: string) => {
    try {
      const articleUserReactionService = new ArticleUserReactionService({
        credentials: 'include'
      });
      const articleUserReaction =
        await articleUserReactionService.getAtreusArticlesUserReaction({
          articleSlug: slug
        });
      set({
        isLikeActive: articleUserReaction.kind === 'like',
        isDislikeActive: articleUserReaction.kind === 'dislike'
      });
    } catch {
      set({ isLikeActive: false, isDislikeActive: false });
    }
  },

  toggleIsLikeActive: async (slug: string) => {
    try {
      const articleUserReactionService = new ArticleUserReactionService({
        credentials: 'include'
      });
      const articleUserReaction =
        await articleUserReactionService.getAtreusArticlesUserReaction({
          articleSlug: slug
        });
      const reaction: boolean = articleUserReaction?.reaction;

      if (reaction) {
        return;
      }

      const reactArticleService = new ReactArticleService({
        credentials: 'include'
      });
      await reactArticleService.putAtreusArticlesReact({
        articleSlug: slug,
        requestBody: {
          kind: 'like'
        }
      });
      set(prev => ({
        ...prev,
        isLikeActive: true,
        isAuthenticated: true,
        likes: (prev.likes ?? 0) + 1
      }));
    } catch {
      set({ isLikeActive: false });
    }
  },

  toggleIsDislikeActive: async (slug: string) => {
    try {
      const articleUserReactionService = new ArticleUserReactionService({
        credentials: 'include'
      });
      const articleUserReaction =
        await articleUserReactionService.getAtreusArticlesUserReaction({
          articleSlug: slug
        });
      const reaction: boolean = articleUserReaction?.reaction;

      if (reaction) {
        return;
      }

      const reactArticleService = new ReactArticleService({
        credentials: 'include'
      });
      await reactArticleService.putAtreusArticlesReact({
        articleSlug: slug,
        requestBody: {
          kind: 'dislike'
        }
      });
      set(prev => ({
        ...prev,
        isDislikeActive: true,
        isAuthenticated: true,
        dislikes: (prev.likes ?? 0) + 1
      }));
    } catch {
      set({ isDislikeActive: false });
    }
  },

  setCommentaries: (commentaries: CommentaryWithUserProcessed[]) => {
    set(prev => ({
      ...prev,
      comments: {
        ...prev.comments,
        commentaries
      }
    }));
  },

  setCommentsData: (data: CommentariesResponse) => {
    set({
      comments: data
    });
  },

  updateCommentsData: (data: CommentariesResponse) => {
    set(prev => ({
      ...prev,
      comments: {
        commentaries: [...prev.comments.commentaries, ...data.commentaries],
        meta: data.meta
      }
    }));
  },

  openAlertModal: (alertData: { type: errorsType }) => {
    set(prev => ({
      ...prev,
      alertModal: {
        ...prev.alertModal,
        ...alertData,
        isOpen: true
      }
    }));
  },

  closeAlertModal: () => {
    set(prev => ({
      ...prev,
      alertModal: {
        ...prev.alertModal,
        type: 'default',
        isOpen: false
      }
    }));
  },

  fetchCommentaries: async ({
    page = 1,
    limit = 3
  }: {
    page?: number;
    limit?: number;
  } = {}) => {
    try {
      const currentArticleId = get().articleId;

      if (!currentArticleId) {
        throw new Error('ArticleId não encontrado no estado');
      }

      // Usando path relativo em vez de URL absoluta
      const url = new URL(`/api/comments`, window.location.origin);
      url.searchParams.append('articleId', currentArticleId.toString());
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Falha ao buscar comentários');
      }

      const result = await response.json();

      if (page === 1) {
        set({
          comments: result
        });
      } else {
        set(prev => ({
          ...prev,
          comments: {
            commentaries: [
              ...prev.comments.commentaries,
              ...result.commentaries
            ],
            meta: result.meta
          }
        }));
      }

      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao buscar comentários:', error);
      return {
        commentaries: [],
        meta: { hasMore: false }
      };
    }
  }
}));

export const NewsStoreProvider = Provider;
export const useNewsStore = useStore;
